import type { UserProfile } from './types';

const SESSION_BLOB_KEY = '_ml_sc';
const KEY_MATERIAL_KEY = '_ml_km';

type StoredSession = {
  v: 1;
  u: UserProfile;
  t: number;
};

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function getSubtle(): SubtleCrypto {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Secure storage is not available in this browser.');
  }
  return crypto.subtle;
}

async function importKey(raw: Uint8Array): Promise<CryptoKey> {
  return getSubtle().importKey('raw', raw, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

function readKeyMaterial(): Uint8Array | null {
  try {
    const encoded = localStorage.getItem(KEY_MATERIAL_KEY);
    if (!encoded) {
      return null;
    }
    return base64ToBytes(encoded);
  } catch {
    return null;
  }
}

async function getOrCreateCryptoKey(): Promise<CryptoKey> {
  const existing = readKeyMaterial();
  if (existing) {
    return importKey(existing);
  }

  const raw = crypto.getRandomValues(new Uint8Array(32));
  localStorage.setItem(KEY_MATERIAL_KEY, bytesToBase64(raw));
  return importKey(raw);
}

async function encryptPayload(plainText: string): Promise<string> {
  const key = await getOrCreateCryptoKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plainText);
  const cipher = await getSubtle().encrypt({ name: 'AES-GCM', iv }, key, encoded);
  const packed = new Uint8Array(iv.length + cipher.byteLength);
  packed.set(iv, 0);
  packed.set(new Uint8Array(cipher), iv.length);
  return bytesToBase64(packed);
}

async function decryptPayload(blob: string): Promise<string> {
  const key = await getOrCreateCryptoKey();
  const packed = base64ToBytes(blob);
  const iv = packed.slice(0, 12);
  const cipher = packed.slice(12);
  const plain = await getSubtle().decrypt({ name: 'AES-GCM', iv }, key, cipher);
  return new TextDecoder().decode(plain);
}

export async function saveSession(user: UserProfile): Promise<void> {
  const payload: StoredSession = {
    v: 1,
    u: user,
    t: Date.now(),
  };
  const encrypted = await encryptPayload(JSON.stringify(payload));
  localStorage.setItem(SESSION_BLOB_KEY, encrypted);
}

export async function loadSession(): Promise<UserProfile | null> {
  try {
    const blob = localStorage.getItem(SESSION_BLOB_KEY);
    if (!blob) {
      return null;
    }
    const json = await decryptPayload(blob);
    const parsed = JSON.parse(json) as StoredSession;
    if (!parsed?.u?.id) {
      return null;
    }
    return parsed.u;
  } catch {
    clearSession();
    return null;
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_BLOB_KEY);
  } catch {
    // ignore
  }
}
